/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './RegisSchemes.module.scss';

type Translate = (key: string) => string;

type Country = 'gb' | 'de' | 'fr' | 'nl' | 'us';

type Scheme = {
  country: Country;
  register: string;
  format: string;
  note: string;
};

const getSchemes = (t: Translate): Scheme[] => [
  {
    country: 'gb',
    register: 'Companies House',
    format: '09410808',
    note: t('Eight characters, with SC and NI prefixes for Scotland and Northern Ireland'),
  },
  {
    country: 'de',
    register: 'Handelsregister',
    format: 'HRB 6684',
    note: t('Register court, then HRA or HRB, then a number that repeats across courts'),
  },
  {
    country: 'fr',
    register: 'SIREN',
    format: '552 100 554',
    note: t('Nine digits, extended to fourteen for each individual establishment'),
  },
  {
    country: 'nl',
    register: 'KvK',
    format: '33014286',
    note: t('Eight digits, separate from the VAT and legal entity identifiers'),
  },
  {
    country: 'us',
    register: 'Delaware file number',
    format: '7714902',
    note: t('Seven digits, unique to one state of fifty'),
  },
];

type Diff = {
  ix: string;
  title: string;
  desc: string;
};

const getDiffs = (t: Translate): Diff[] => [
  {
    ix: '01',
    title: t('Entity resolution'),
    desc: t(
      'The same company appears under a trading name in one register, a legal name in another and a transliteration in a third. Resolving a question to a registration number is the step that decides whether the rest of the answer is about the right company.',
    ),
  },
  {
    ix: '02',
    title: t('Field definitions'),
    desc: t(
      'Turnover is not the same line in every accounting standard, and a director is not the same office in every company law. Answers are normalised to a common dictionary while the original value stays attached.',
    ),
  },
  {
    ix: '03',
    title: t('Document extraction'),
    desc: t(
      'A large share of registry filings are documents rather than data. Financial statements, shareholder registers and charge filings are read out of the filed document and structured into fields.',
    ),
  },
  {
    ix: '04',
    title: t('Update cadence'),
    desc: t(
      "Registers publish on their own schedules, from continuous to annual. Records are synced daily, and the retrieval timestamp tells you which side of a register's update you are on.",
    ),
  },
  {
    ix: '05',
    title: t('Ownership traversal'),
    desc: t(
      'A beneficial owner three jurisdictions up is a chain of registry lookups, not a field. Regis walks the chain and returns each hop with the register it was read from.',
    ),
  },
];

const FlagSprite = () => (
  <svg aria-hidden="true" className={styles.sprite} width="0" height="0">
    <defs>
      <symbol id="sc-gb" viewBox="0 0 16 11">
        <rect fill="#012169" width="16" height="11" />
        <path d="M0 0l16 11M16 0L0 11" stroke="#fff" strokeWidth="2.2" />
        <path d="M0 0l16 11M16 0L0 11" stroke="#C8102E" strokeWidth="1.1" />
        <path d="M8 0v11M0 5.5h16" stroke="#fff" strokeWidth="3.4" />
        <path d="M8 0v11M0 5.5h16" stroke="#C8102E" strokeWidth="2" />
      </symbol>
      <symbol id="sc-de" viewBox="0 0 16 11">
        <rect fill="#000" width="16" height="3.67" />
        <rect fill="#DD0000" width="16" height="3.67" y="3.67" />
        <rect fill="#FFCE00" width="16" height="3.66" y="7.34" />
      </symbol>
      <symbol id="sc-fr" viewBox="0 0 16 11">
        <rect fill="#fff" width="16" height="11" />
        <rect fill="#002395" width="5.34" height="11" />
        <rect fill="#ED2939" width="5.34" height="11" x="10.66" />
      </symbol>
      <symbol id="sc-nl" viewBox="0 0 16 11">
        <rect fill="#21468B" width="16" height="11" />
        <rect fill="#AE1C28" width="16" height="3.67" />
        <rect fill="#fff" width="16" height="3.67" y="3.67" />
      </symbol>
      <symbol id="sc-us" viewBox="0 0 16 11">
        <rect fill="#B22234" width="16" height="11" />
        <rect fill="#fff" y="0.85" width="16" height="0.85" />
        <rect fill="#fff" y="2.53" width="16" height="0.85" />
        <rect fill="#fff" y="4.23" width="16" height="0.85" />
        <rect fill="#fff" y="5.92" width="16" height="0.85" />
        <rect fill="#fff" y="7.61" width="16" height="0.85" />
        <rect fill="#fff" y="9.3" width="16" height="0.85" />
        <rect fill="#3C3B6E" width="7" height="5.9" />
      </symbol>
    </defs>
  </svg>
);

export const RegisSchemes = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);
  const visible = useIntersection(rootRef, { threshold: 0.1 });

  const schemes = getSchemes(t);
  const diffs = getDiffs(t);

  return (
    <Section className={styles.root}>
      <FlagSprite />

      <div ref={rootRef} className={cn(styles.content, visible && styles.contentIn)}>
        <div className={styles.head}>
          <TitleBlock
            align="left"
            title={t('Four hundred registers do not agree with each other.')}
            description={t(
              'Plain-language querying is the surface. What makes it answerable is the part that took years: reconciling registers that publish different fields, in different languages, on different schedules, under different definitions of the same word.',
            )}
          />
        </div>

        <div className={styles.schemes}>
          {schemes.map((scheme) => (
            <div key={scheme.register} className={styles.scheme}>
              <div className={styles.schemeHead}>
                <svg className={styles.flag} viewBox="0 0 16 11" aria-hidden="true">
                  <use href={`#sc-${scheme.country}`} />
                </svg>
                <span className={styles.schemeReg}>{scheme.register}</span>
              </div>

              <span className={styles.schemeFormat}>{scheme.format}</span>
              <span className={styles.schemeNote}>{scheme.note}</span>
            </div>
          ))}
        </div>

        <p className={styles.caption}>
          {t(
            'Five incompatible ways of naming the same class of object. None of them was designed to interoperate with the others.',
          )}
        </p>

        <div className={styles.diffs}>
          {diffs.map((diff) => (
            <div key={diff.ix} className={styles.diff}>
              <span className={styles.diffIx}>{diff.ix}</span>
              <div>
                <h3 className={styles.diffTitle}>{diff.title}</h3>
                <p className={styles.diffText}>{diff.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
